import { visibilityUtils } from "@juicetechnologyag/webapp-platform-api";
import React from "react";
import { useIntl } from "react-intl";
import useErrorAlert from "../../hooks/useErrorAlert";
import usePolling from "../../hooks/usePolling";
import DeviceInterface from "../../interfaces/DeviceInterface";
import GroupInterface from "../../interfaces/GroupInterface";
import useFetch from "../../pages/group/useFetch";
import BackendService from "../../service/backend.service";
import CustomError from "../../service/errors/custom.error";
import webappPlatformApi from "../../webapp-platform-api/webappPlatformApi";
import deviceFactory from "../factory/deviceFactory";
import groupFactory from "../factory/groupFactory";
import { useLoading } from "../loading/LoadingContextStore";

const POLLING_INTERVAL = 1 * 1000;
const DEFAULT_POLLING_INTERVAL = 10 * 1000;

function useDataStore() {
  const showErrorAlert = useErrorAlert();
  const { formatMessage } = useIntl();
  const processingAsyncFn = useLoading();
  const [backendService] = React.useState(BackendService());

  const [_deviceStore, changeDeviceStore] = React.useState<any[] | undefined>(
    undefined
  );

  const [_groupStore, changeGroupStore] = React.useState<any[] | undefined>(
    undefined
  );

  const deviceStore = React.useMemo<DeviceInterface[]>(() => {
    if (_deviceStore == null) return [];

    return _deviceStore.map((device) => {
      const deviceGroup = _groupStore?.find((g) => {
        const isDeviceGroup = g.devices?.some(
          (gd: { deviceId: string; deviceType: string }) => {
            return gd.deviceId === device.id && gd.deviceType === device.type;
          }
        );
        return isDeviceGroup ? g : undefined;
      });

      return deviceFactory({
        groupId: deviceGroup?.id,
        ...device,
      });
    });
  }, [_deviceStore, _groupStore]);

  const groupStore = React.useMemo(() => {
    if (_groupStore == null) return [];

    return _groupStore.map((g) =>
      groupFactory({
        ...g,
        devices: deviceStore.filter((d) => d.groupId === g.id),
      })
    );
  }, [_groupStore, deviceStore]);

  const _fetchDevices = React.useCallback(async () => {
    const devices = await backendService.findAllDevice();
    changeDeviceStore(devices);
  }, [backendService]);

  const _fetchGroups = React.useCallback(async () => {
    const groups = await backendService.findAllGroup();
    changeGroupStore(groups);
  }, [backendService]);

  const _handlingCustomErrors = React.useCallback(
    (e: Error, method: string, param: any = undefined) => {
      let message = e.message;
      if (e instanceof CustomError) {
        switch (e.getCode()) {
          case 406:
            message = formatMessage({
              id:
                method === "removeGroup"
                  ? "group.remove.not_allowed"
                  : "group.create_device.limit_error",
            });
            break;
          case 409:
            message =
              param != null
                ? formatMessage({ id: "device.enabling.error_text" })
                : formatMessage({ id: "device.disabling.error_text" });
            break;
          default:
            break;
        }
        throw new CustomError(e.getCode(), message);
      }
    },
    [formatMessage]
  );

  const saveDeviceGroup = React.useCallback(
    async (
      deviceId: string,
      deviceType: string,
      groupId: string | undefined
    ) => {
      try {
        await backendService.saveDeviceToGroup(deviceId, deviceType, groupId);
      } catch (e: any) {
        console.error("saveDeviceToGroup failed: ", e);
        _handlingCustomErrors(e, "saveDeviceGroup", groupId);
        throw new Error(e.message);
      }
      await _fetchGroups();
    },
    [backendService, _fetchGroups, _handlingCustomErrors]
  );

  const saveGroup = React.useCallback(
    async (groupId: string | undefined, name: string, limit: number) => {
      try {
        const group = await backendService.saveGroup(groupId, name, limit);

        changeGroupStore((groups: GroupInterface[] | undefined) => [
          ...(groups?.filter((g) => g.id !== groupId) ?? []),
          group,
        ]);

        return group.id;
      } catch (e: any) {
        console.error("saveGroup failed: ", e);
        _handlingCustomErrors(e, "saveGroup", groupId);
        throw new Error(e.message);
      }
    },
    [backendService, _handlingCustomErrors]
  );

  const removeGroup = React.useCallback(
    async (groupId: string) => {
      try {
        await backendService.removeGroup(groupId);
        changeGroupStore((groups: GroupInterface[] | undefined) => {
          return groups?.filter((g) => g.id !== groupId) ?? [];
        });
      } catch (e: any) {
        console.error("removeGroup failed: ", e);
        _handlingCustomErrors(e, "removeGroup");
        throw new Error(e.message);
      }
      await _fetchDevices();
    },
    [backendService, _fetchDevices, _handlingCustomErrors]
  );

  const _fetchData = React.useCallback(async () => {
    try {
      if (!webappPlatformApi.auth.authenticated) return;
      await Promise.all([_fetchDevices(), _fetchGroups()]);
    } catch (e) {
      console.error("Error in initial fetch", e);
      await showErrorAlert(undefined, true);
    }
  }, [_fetchDevices, _fetchGroups, showErrorAlert]);

  const dataStorePolling = usePolling(_fetchData, DEFAULT_POLLING_INTERVAL);

  React.useEffect(() => {
    dataStorePolling.changeInterval(
      (deviceStore?.length ?? 0) > 0
        ? POLLING_INTERVAL
        : DEFAULT_POLLING_INTERVAL
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceStore]);

  const fetchData = React.useCallback(async () => {
    await processingAsyncFn(_fetchData).then(dataStorePolling.start);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_fetchData, processingAsyncFn]);

  useFetch(fetchData);

  React.useEffect(() => {
    const listener = async (v: boolean) => {
      if (!v) return;
      await fetchData();
    };

    visibilityUtils.addEventListener(listener);

    return () => {
      visibilityUtils.removeEventListener(listener);
    };
  }, [fetchData]);

  return {
    deviceStore,
    groupStore,
    saveDeviceGroup,
    saveGroup,
    removeGroup,
  };
}

export default useDataStore;
